import {Card, Container, Divider, Grid, Group, Table, Title} from "@mantine/core";
import {useEffect, useState} from "react";
import {
  buildStandardHeaders,
  CompanyEntity,
  CompanyUnitEntity,
  ContainerEntity,
  firstOrUndefined,
  FreightTypeEntity,
  OtsAsyncThunkConfigExtra,
  ServiceTypeEntity,
  SiteEntity,
  transportCategory,
  TransportCategory,
  TransportType,
  transportType
} from "@ots-frontend/ots-data";
import {BaseQueryApi} from "@reduxjs/toolkit/query";
import {useApp} from "../../providers/RegionProvider/RegionProvider";
import {ThemedPage} from "../../components/page/ThemedPage/ThemedPage";
import {ResizeableContent} from "../../components/ResizableContent/ResizableContent";
import {useTranslation} from "react-i18next";
import {TransportTypeSelect} from "../../components/select/entity/TransportTypeSelect/TransportTypeSelect";
import {FormattedDatabaseCode} from "../../components/text/FormattedDatabaseCode/FormattedDatabaseCode";
import {TransportCategorySelect} from "../../components/select/entity/TransportCategorySelect/TransportCategorySelect";
import {CompanySelect} from "../../components/select/entity/CompanySelect/CompanySelect";
import {FreightTypeSelect} from "../../components/select/entity/FreightTypeSelect/FreightTypeSelect";
import {ServiceTypeSelect} from "../../components/select/entity/ServiceTypeSelect/ServiceTypeSelect";
import {SiteSelect} from "../../components/select/entity/SiteSelect/SiteSelect";
import {ContainerSelect} from "../../components/select/entity/ContainerSelect/ContainerSelect";
import {CompanyUnitSelect} from "../../components/select/entity/CompanyUnitSelect/CompanyUnitSelect";

export type TransportFormPreferencePageProps = {}

export const resolvePreferences = (
  type: TransportType,
  category: TransportCategory,
  regionId: string,
  companyId: string | undefined,
  companyUnitId: string | undefined,
  siteId: string | undefined,
  containerId: string | undefined,
  freightTypeId: string | undefined,
  serviceTypeId: string | undefined,
  api: BaseQueryApi
): Promise<Response> => {
  const headers = buildStandardHeaders(api)
  headers["Content-Type"] = 'application/json'

  const request: RequestInit = {
    method: 'GET',
    headers: headers,
  }

  const baseUrl = (api.extra as OtsAsyncThunkConfigExtra).baseUrl;
  let url = `${baseUrl}/preferences/resolve-transport-form?transportType=${type.code}&transportCategory=${category.code}`;

  if (regionId) {
    url += `&regionId=${regionId}`;
  }

  if (companyId) {
    url += `&companyId=${companyId}`;
  }

  if (companyUnitId) {
    url += `&companyUnitId=${companyUnitId}`;
  }

  if (siteId) {
    url += `&siteId=${siteId}`;
  }

  if (containerId) {
    url += `&containerId=${containerId}`;
  }

  if (freightTypeId) {
    url += `&freightTypeId=${freightTypeId}`;
  }

  if (serviceTypeId) {
    url += `&serviceTypeId=${serviceTypeId}`;
  }

  return window.fetch(url, request)
}

export const TransportFormPreferencePageRoute = () => {
  return (
    <TransportFormPreferencePage/>
  );
};

export const TransportFormPreferencePage = (props: TransportFormPreferencePageProps) => {

  const {t} = useTranslation(["translation", "common"])
  const {appCategory, regionId, baseQueryApi} = useApp();

  const [type, setType] = useState<TransportType>(transportType.pickup)
  const [category, setCategory] = useState<TransportCategory>(appCategory ?? transportCategory.water)
  const [company, setCompany] = useState<CompanyEntity | undefined>();
  const [companyUnit, setCompanyUnit] = useState<CompanyUnitEntity | undefined>();
  const [site, setSite] = useState<SiteEntity | undefined>();
  const [container, setContainer] = useState<ContainerEntity | undefined>();
  const [freightType, setFreightType] = useState<FreightTypeEntity | undefined>();
  const [serviceType, setServiceType] = useState<ServiceTypeEntity | undefined>();
  const [fieldProperties, setFieldProperties] = useState(undefined);

  useEffect(() => {
    if (regionId) {
      resolvePreferences(
        type,
        category,
        regionId,
        company?.id,
        companyUnit?.id,
        site?.id,
        container?.id,
        freightType?.id,
        serviceType?.id,
        baseQueryApi
      )
        .then(res => res.json())
        .then(data => setFieldProperties(data))
        .catch(err => console.error('Error fetching preferences:', err));
    } else {
      setFieldProperties(null);
    }
  }, [
    type,
    category,
    company,
    companyUnit,
    site,
    container,
    freightType,
    serviceType,
    regionId,
    baseQueryApi
  ]);

  const tableRows = fieldProperties
    ? Object.entries(fieldProperties).map(([field, props]) => (
      <tr key={field}>
        <td>{field.charAt(0).toUpperCase() + field.slice(1)}</td>
        <td>{props["visible"] ? <b>Yes</b> : 'No'}</td>
        <td>{props["required"] ? <b>Yes</b> : 'No'}</td>
      </tr>
    ))
    : null;

  return (
    <ThemedPage>
      {(classes) => (
        <ResizeableContent>
          {(size) => (
            <Container h={size.height} w={size.width} className={classes.container} fluid={true} p="md">
              <Card shadow="sm" padding="lg" m="sm" radius="md" withBorder>
                <Group position="apart">
                  <Title order={4}>
                    {t("common.page.preferences.transportform", "Transport Form Preferences")}
                  </Title>
                </Group>
              </Card>
              <Grid>
                <Grid.Col span={8}>
                  <Container fluid={true}>
                    <Card shadow="sm" padding="lg" mb="lg" radius="md" withBorder>
                      <Title order={2} mb="md">
                        <Group spacing="sm">
                          <FormattedDatabaseCode value={category}/>
                          {" "}
                          <FormattedDatabaseCode value={type}/>
                          {" Form"}
                        </Group>
                      </Title>
                      <Table highlightOnHover>
                        <thead>
                        <tr>
                          <th>Field</th>
                          <th>Visible</th>
                          <th>Required</th>
                        </tr>
                        </thead>
                        <tbody>{tableRows}</tbody>
                      </Table>
                    </Card>
                  </Container>
                </Grid.Col>
                <Grid.Col span={4}>
                  <Container fluid={true}>
                    <Card shadow="sm" padding="lg" mb="lg" radius="md" withBorder>
                      <TransportTypeSelect
                        onDbCodeChange={e => setType(firstOrUndefined(e) ?? transportType.pickup)}
                        value={type.code}
                        label={t("common.entity.transport.type", "Type")}
                        placeholder={t<string>("common.entity.transport.type", "Type")}
                        withinPortal={true}
                        clearable
                        withAsterisk
                        required/>
                      <TransportCategorySelect
                        onDbCodeChange={e => setCategory(firstOrUndefined(e) ?? appCategory ?? transportCategory.water)}
                        value={category.code}
                        label={t("common.entity.transport.category", "Category")}
                        placeholder={t<string>("common.entity.transport.category", "Category")}
                        withinPortal={true}
                        clearable
                        withAsterisk
                        required/>
                      <Divider my="sm" variant="dotted"/>
                      <CompanySelect
                        selector={{
                          regionId: regionId
                        }}
                        onEntityChange={(e) => setCompany(firstOrUndefined(e))}
                        value={company?.id}
                        label={t("common.entity.company", "Company")}
                        placeholder={t<string>("common.entity.company", "Company")}
                        withinPortal={true}
                        clearable/>
                      <CompanyUnitSelect
                        selector={{
                          regionId: regionId
                        }}
                        onEntityChange={(e) => setCompanyUnit(firstOrUndefined(e))}
                        value={company?.id}
                        label={t("common.entity.companyunit", "Company Unit")}
                        placeholder={t<string>("common.entity.companyunit", "Company Unit")}
                        withinPortal={true}
                        clearable/>
                      <SiteSelect
                        selector={{
                          regionId: regionId
                        }}
                        onEntityChange={(e) => setSite(firstOrUndefined(e))}
                        value={company?.id}
                        label={t("common.entity.site", "Site")}
                        placeholder={t<string>("common.entity.site", "Site")}
                        withinPortal={true}
                        clearable/>
                      <ContainerSelect
                        selector={{
                          regionId: regionId
                        }}
                        onEntityChange={(e) => setContainer(firstOrUndefined(e))}
                        value={container?.id}
                        label={t("common.entity.container", "Container")}
                        placeholder={t<string>("common.entity.container", "Container")}
                        withinPortal={true}
                        clearable/>
                      <FreightTypeSelect
                        selector={{
                          regionId: regionId
                        }}
                        onEntityChange={(e) => setFreightType(firstOrUndefined(e))}
                        value={freightType?.id}
                        label={t("common.entity.freighttype", "Freight Type")}
                        placeholder={t<string>("common.entity.freighttype", "Freight Type")}
                        withinPortal={true}
                        clearable/>
                      <ServiceTypeSelect
                        selector={{
                          regionId: regionId
                        }}
                        onEntityChange={(e) => setServiceType(firstOrUndefined(e))}
                        value={serviceType?.id}
                        label={t("common.entity.servicetype", "Service Type")}
                        placeholder={t<string>("common.entity.servicetype", "Service Type")}
                        withinPortal={true}
                        clearable/>
                    </Card>
                  </Container>
                </Grid.Col>
              </Grid>
            </Container>
          )}
        </ResizeableContent>
      )}
    </ThemedPage>
  );
};
